import React, { useState, useMemo } from 'react';
import {
  STAGE_NOTIFICATION_TYPES_BY_STAGE,
  STAGE_NOTIFICATION_TYPE_LABELS,
  CANDIDATE_STAGE_LABELS,
} from '@onboarding-dashboard/shared';
import type { CandidateStage, StageNotificationType } from '@onboarding-dashboard/shared';
import { adminService } from '../../services/admin.js';
import './diagnostic-panel.css';

interface TestStageNotificationResult {
  templatePath?: string;
  templateExists?: boolean;
  notificationTypeFound?: boolean;
  knownNotificationTypes?: string[];
  contentEntry?: unknown;
  sendResult?: unknown;
  errorMessage?: string;
  errorStack?: string;
}

interface DiagnosticPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export function DiagnosticPanel({
  isOpen,
  onClose,
  userEmail,
}: DiagnosticPanelProps): React.ReactElement | null {
  const [selectedStage, setSelectedStage] = useState<CandidateStage | ''>('');
  const [selectedNotificationType, setSelectedNotificationType] = useState<
    StageNotificationType | ''
  >('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<TestStageNotificationResult | null>(null);

  const stages = useMemo(() => {
    return Object.keys(STAGE_NOTIFICATION_TYPES_BY_STAGE ?? {}) as CandidateStage[];
  }, []);

  const notificationTypes = useMemo(() => {
    if (!selectedStage) return [];
    return STAGE_NOTIFICATION_TYPES_BY_STAGE[selectedStage] ?? [];
  }, [selectedStage]);

  const handleStageChange = (stage: CandidateStage | ''): void => {
    setSelectedStage(stage);
    setSelectedNotificationType('');
    setResult(null);
  };

  const handleSend = async (): Promise<void> => {
    if (!userEmail || !selectedStage || !selectedNotificationType) return;
    setIsSending(true);
    setResult(null);
    try {
      const response = await adminService.testStageNotification(
        selectedNotificationType,
        selectedStage
      );
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setResult({
          errorMessage: response.error?.message ?? 'Request failed',
        });
      }
    } catch {
      setResult({ errorMessage: 'Network error' });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  const canSend = selectedStage !== '' && selectedNotificationType !== '' && !isSending;

  return (
    <div className="diagnostic-overlay" onClick={onClose}>
      <div className="diagnostic-panel" onClick={(e) => e.stopPropagation()}>
        <div className="diagnostic-panel-header">
          <h2>System Diagnostics</h2>
          <button className="diagnostic-close-btn" onClick={onClose}>
            X
          </button>
        </div>

        <p className="diagnostic-section-title">Test Stage Notification Email</p>

        <p className="diagnostic-recipient">Sending to: {userEmail}</p>

        <div className="diagnostic-field">
          <label>Stage</label>
          <select
            value={selectedStage}
            onChange={(e) => handleStageChange(e.target.value as CandidateStage | '')}
          >
            <option value="">Select a stage</option>
            {stages.map((stage) => (
              <option key={stage} value={stage}>
                {CANDIDATE_STAGE_LABELS[stage] ?? stage}
              </option>
            ))}
          </select>
        </div>

        <div className="diagnostic-field">
          <label>Notification Type</label>
          <select
            value={selectedNotificationType}
            onChange={(e) =>
              setSelectedNotificationType(e.target.value as StageNotificationType | '')
            }
            disabled={notificationTypes.length === 0}
          >
            <option value="">Select a type</option>
            {notificationTypes.map((nt) => (
              <option key={nt} value={nt}>
                {STAGE_NOTIFICATION_TYPE_LABELS[nt] ?? nt}
              </option>
            ))}
          </select>
        </div>

        <button
          className="diagnostic-send-btn"
          disabled={!canSend}
          onClick={() => void handleSend()}
        >
          {isSending ? 'Sending...' : 'Send Test'}
        </button>

        {result && (
          <div className="diagnostic-results">
            <p className="diagnostic-results-title">Results</p>

            {result.templatePath != null && (
              <>
                <div className="diagnostic-result-row">
                  <span className={result.templateExists ? 'diagnostic-check' : 'diagnostic-cross'}>
                    {result.templateExists ? '✓' : '✗'}
                  </span>
                  <span>Template exists</span>
                </div>
                <p className="diagnostic-result-path">{result.templatePath}</p>
              </>
            )}

            {result.notificationTypeFound != null && (
              <div className="diagnostic-result-row">
                <span
                  className={result.notificationTypeFound ? 'diagnostic-check' : 'diagnostic-cross'}
                >
                  {result.notificationTypeFound ? '✓' : '✗'}
                </span>
                <span>Notification type found</span>
              </div>
            )}

            {result.sendResult != null && !result.errorMessage && (
              <p className="diagnostic-result-success">Email sent successfully</p>
            )}

            {result.errorMessage && (
              <p className="diagnostic-result-error">{result.errorMessage}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
